import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, LayoutGrid, Star, Gamepad2, Sparkles, LogOut, ChevronDown, Search as SearchIcon } from "lucide-react";
import { signOut } from "firebase/auth";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { auth } from "../firebase";
import UserAvatar from "./UserAvatar";
import NotificationBell from "./NotificationBell";
import PipAnchor from "./Pip/PipAnchor";
import { getProfileNavPath } from "../utils/navPaths";
import { LOGGED_OUT_SIGNIN_PATH } from "./profile/LoggedOutProfileGate";
import "./Nav.css";
import "./NotificationDropdown.css";

export const APP_NAV_ITEMS = [
  {
    label: "Home",
    path: "/",
    icon: Home,
    isActive: (pathname) =>
      pathname === "/" ||
      pathname === "/home" ||
      pathname === "/following" ||
      pathname.startsWith("/notifications"),
  },
  {
    label: "Gallery",
    path: "/gallery",
    icon: LayoutGrid,
    isActive: (pathname) =>
      pathname === "/gallery" ||
      pathname === "/discover" ||
      pathname.startsWith("/post/") ||
      pathname.startsWith("/drop/"),
  },
  {
    label: "Featured",
    path: "/featured",
    icon: Star,
    isActive: (pathname) =>
      pathname === "/featured" || pathname.startsWith("/featured/"),
  },
  {
    label: "Games",
    path: "/games",
    icon: Gamepad2,
    isActive: (pathname) => pathname === "/games" || pathname.startsWith("/games/"),
  },
  {
    label: "Perks",
    path: "/perks",
    icon: Sparkles,
    isActive: (pathname) => pathname === "/perks" || pathname.startsWith("/perks/"),
  },
];

function navItemTo(path, signedIn) {
  if (signedIn || path === "/games") return path;
  return LOGGED_OUT_SIGNIN_PATH;
}

function navLinkClass(isActive) {
  return `app-nav-link${isActive ? " app-nav-link--active" : ""}`;
}

export default function Nav() {
  const { currentUser, userProfile } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const menuRef = useRef(null);

  const onLoggedOutProfile =
    !currentUser &&
    (pathname.startsWith("/creator/") || pathname.startsWith("/fan/"));
  const logoTo = currentUser
    ? "/"
    : onLoggedOutProfile
      ? LOGGED_OUT_SIGNIN_PATH
      : "/welcome";

  const profilePath = getProfileNavPath(currentUser, userProfile);

  const avatarSrc = userProfile?.avatar || userProfile?.avatarUrl || "";

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      await signOut(auth);
      navigate("/welcome");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  return (
    <>
      <header className="app-nav-top" aria-label="Main navigation">
        <PipAnchor id="nav-logo">
          <Link to={logoTo} className="app-nav-logo" aria-label="100ViewsOnly home">
            <Logo />
          </Link>
        </PipAnchor>

        <nav className="app-nav-center" aria-label="Primary">
          <PipAnchor id="nav-home-gallery" style={{ display: "contents" }}>
            {APP_NAV_ITEMS.slice(0, 2).map(({ label, path, isActive }) => (
              <NavLink
                key={path}
                to={navItemTo(path, currentUser)}
                end={path === "/"}
                className={() => navLinkClass(isActive(pathname))}
              >
                {label}
              </NavLink>
            ))}
          </PipAnchor>
          {APP_NAV_ITEMS.slice(2).map(({ label, path, isActive }) => (
            <NavLink
              key={path}
              to={navItemTo(path, currentUser)}
              end={path === "/"}
              className={() => navLinkClass(isActive(pathname))}
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="app-nav-actions">
          <PipAnchor id="nav-search">
            <Link
              to="/search"
              className={`nav-btn app-nav-icon-btn app-nav-search-btn${pathname === "/search" || pathname.startsWith("/search?") ? " app-nav-search-btn--active" : ""}`}
              aria-label="Search"
              onClick={() => {
                setMenuOpen(false);
                setNotifOpen(false);
              }}
            >
              <SearchIcon size={16} strokeWidth={2} aria-hidden="true" />
            </Link>
          </PipAnchor>
          <PipAnchor id="nav-bell">
            <NotificationBell
              isOpen={notifOpen}
              onOpenChange={(open) => {
                setNotifOpen(open);
                if (open) setMenuOpen(false);
              }}
              buttonClassName="nav-btn app-nav-icon-btn"
              wrapClassName="nav-notif-wrap app-nav-notif-wrap"
            />
          </PipAnchor>

          <div className="app-nav-avatar-wrap" ref={menuRef}>
            <PipAnchor id="nav-avatar">
              <Link
                to={profilePath}
                className="app-nav-avatar"
                aria-label="View profile"
                onClick={() => {
                  setMenuOpen(false);
                  setNotifOpen(false);
                }}
              >
                <UserAvatar
                  src={avatarSrc}
                  alt={userProfile?.displayName || ""}
                  name={userProfile?.displayName || currentUser?.displayName || ""}
                  className="app-nav-avatar-img"
                />
              </Link>
            </PipAnchor>
            {currentUser && (
              <PipAnchor id="nav-chevron">
                <button
                  type="button"
                  className="app-nav-avatar-menu-btn"
                  aria-label="Account menu"
                  aria-expanded={menuOpen}
                  onClick={() => {
                    setMenuOpen((open) => !open);
                    setNotifOpen(false);
                  }}
                >
                  <ChevronDown size={14} strokeWidth={2} aria-hidden="true" />
                </button>
              </PipAnchor>
            )}

            {menuOpen && (
              <div className="app-nav-avatar-menu" role="menu">
                <Link
                  to="/settings"
                  className="app-nav-avatar-menu-item"
                  role="menuitem"
                  onClick={() => setMenuOpen(false)}
                >
                  Settings
                </Link>
                <button
                  type="button"
                  className="app-nav-avatar-menu-item app-nav-avatar-menu-item--danger"
                  role="menuitem"
                  onClick={handleLogout}
                >
                  <LogOut size={16} strokeWidth={2} aria-hidden="true" />
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <PipAnchor id="mobile-bottom-nav">
        <nav className="app-nav-bottom" aria-label="Mobile navigation">
          {APP_NAV_ITEMS.map(({ label, path, icon: Icon, isActive }) => (
            <NavLink
              key={path}
              to={navItemTo(path, currentUser)}
              end={path === "/"}
              className={() =>
                `app-nav-bottom-item${isActive(pathname) ? " app-nav-bottom-item--active" : ""}`
              }
            >
              <Icon size={22} strokeWidth={2} aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </PipAnchor>
    </>
  );
}
